import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { FadeUp, Overline, MaskedLine } from "../components/Reveal";
import { EditorialMarquee } from "../components/Marquee";
import { IMAGES } from "../lib/site";

const CHAPTERS = [
  {
    n: "01",
    color: "#E0146B",
    title: "Ideas today.",
    text: "Every great product starts as a rough sketch and a stubborn belief. We exist to take that sketch seriously — to prototype fast, test honestly, and turn conviction into working software before the moment passes.",
  },
  {
    n: "02",
    color: "#0096C7",
    title: "Engineering with intent.",
    text: "We don't write code to fill sprints. Every architecture decision, every interface detail, every millisecond of load time is a deliberate choice in service of the people who will use what we build.",
  },
  {
    n: "03",
    color: "#FF8A00",
    title: "Craft over cargo cult.",
    text: "No bloated stacks, no buzzword bingo. We pick tools that fit the problem, sweat the last two percent, and treat maintainability as a feature — because software you can't evolve is software that's already dead.",
  },
  {
    n: "04",
    color: "#7C3AED",
    title: "Impact tomorrow.",
    text: "Launch day is the starting line. We measure ourselves by what our products do in the world — the hours they save, the businesses they grow, the barriers they remove. A better tomorrow, shipped one release at a time.",
  },
];

const AboutHero = () => (
  <section data-testid="about-hero" className="pt-48 pb-24 md:pb-32 relative overflow-hidden">
    <div className="aurora aurora-float w-[22rem] h-[22rem] -top-16 -right-16 opacity-25 -z-10" style={{ background: "#0D9488" }} aria-hidden="true" />
    <div className="aurora aurora-float w-[18rem] h-[18rem] bottom-0 -left-20 opacity-20 -z-10" style={{ background: "#E0146B", animationDelay: "6s" }} aria-hidden="true" />
    <div className="absolute -left-40 top-40 w-[24rem] h-[24rem] rounded-full border border-magenta/20 orbit-ring -z-10 hidden lg:block" aria-hidden="true">
      <span className="absolute -top-2 left-1/2 w-4 h-4 rounded-full bg-magenta drop-shadow-[0_0_15px_rgba(224,20,107,0.6)]" />
    </div>
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <MaskedLine delay={0.15}>
        <Overline>About Inubaan Labs</Overline>
      </MaskedLine>
      <h1 className="mt-8 font-display font-black text-[11vw] md:text-[6.8vw] leading-[0.92] tracking-tighter" data-testid="about-heading">
        <MaskedLine delay={0.3}>GREAT IDEAS DESERVE</MaskedLine>
        <MaskedLine delay={0.45} innerClassName="brand-gradient-text">GREAT TECHNOLOGY.</MaskedLine>
      </h1>
      <MaskedLine delay={0.65} className="max-w-xl mt-10">
        <p className="text-base md:text-lg text-slate-600 leading-relaxed">
          Inubaan Labs is a software and technology company creating innovative web apps,
          mobile apps, software, and online services designed to help businesses grow.
        </p>
      </MaskedLine>
    </div>
  </section>
);

const Story = () => (
  <section data-testid="about-story" className="pb-32 md:pb-40">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
      <FadeUp className="md:col-span-7">
        <Overline>Our story</Overline>
        <p className="mt-8 text-base md:text-lg text-slate-600 leading-relaxed">
          Inubaan Labs is a software and technology company dedicated to creating innovative
          web apps, mobile apps, software, and online services designed to help businesses
          grow, operate more efficiently, and succeed in the digital world.
        </p>
        <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed">
          Founded and owned by <span className="text-ink font-semibold">Jafar Rizvi</span>, Inubaan Labs
          focuses on turning ideas into practical, user-friendly digital products. From custom
          web applications and mobile platforms to business-focused online services, we build
          technology with real-world businesses and their customers in mind.
        </p>
      </FadeUp>
      <FadeUp delay={0.15} className="md:col-span-5">
        <div
          data-testid="founder-card"
          className="h-full border rounded-2xl p-10 flex flex-col justify-between transition-transform duration-500 hover:-translate-y-1"
          style={{ backgroundColor: "#7C3AED0D", borderColor: "#7C3AED30" }}
        >
          <img
            src="/founder.webp"
            alt="Jafar Rizvi — Founder & Owner of Inubaan Labs"
            data-testid="founder-photo"
            className="w-28 h-28 rounded-2xl object-cover object-top border-2"
            style={{ borderColor: "#7C3AED40" }}
          />
          <div className="mt-16">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: "#7C3AED" }}>
              Founder & Owner
            </p>
            <p className="mt-2 font-display font-bold text-3xl tracking-tight">Jafar Rizvi</p>
            <p className="mt-3 text-slate-600 text-sm leading-relaxed">
              Turning ideas into practical, user-friendly digital products for real-world
              businesses and their customers.
            </p>
          </div>
        </div>
      </FadeUp>
    </div>
  </section>
);

const Mission = () => (
  <section data-testid="about-mission" className="py-24 md:py-36 border-y border-ink/10 relative overflow-hidden">
    <div className="aurora aurora-float w-[20rem] h-[20rem] -top-10 left-1/4 opacity-20 -z-10" style={{ background: "#FF8A00" }} aria-hidden="true" />
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <FadeUp>
        <Overline>Our mission</Overline>
        <p className="mt-8 font-display font-bold text-3xl md:text-5xl tracking-tighter leading-[1.15] max-w-5xl">
          "To create <span className="brand-gradient-text">powerful, accessible, and innovative</span> technology
          that helps businesses move forward."
        </p>
      </FadeUp>
    </div>
  </section>
);

const Manifesto = () => (
  <section data-testid="about-manifesto" className="py-32 md:py-44">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <FadeUp className="mb-16">
        <Overline>The manifesto</Overline>
      </FadeUp>
      {CHAPTERS.map((c, i) => (
        <FadeUp key={c.n} delay={0.05}>
          <div
            data-testid={`manifesto-chapter-${c.n}`}
            className="group grid grid-cols-12 gap-6 border-t border-ink/10 py-16 md:py-20"
          >
            <span className="col-span-12 md:col-span-2 font-mono text-sm tracking-[0.3em]" style={{ color: c.color }}>
              CH.{c.n}
            </span>
            <h2 className="col-span-12 md:col-span-4 font-display font-bold text-3xl md:text-5xl tracking-tighter transition-transform duration-500 group-hover:translate-x-2">
              {c.title}
            </h2>
            <p className="col-span-12 md:col-span-5 md:col-start-8 text-slate-600 text-base md:text-lg leading-relaxed">
              {c.text}
            </p>
          </div>
        </FadeUp>
      ))}
    </div>
  </section>
);

const StudioImage = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section ref={ref} data-testid="about-studio-image" className="py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="relative overflow-hidden rounded-2xl border border-ink/10 h-[60vh] md:h-[75vh]">
          <motion.img
            src={IMAGES.studio}
            alt="Inside the Inubaan Labs studio"
            style={{ y }}
            className="absolute inset-0 w-full h-[125%] object-cover"
          />
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute bottom-8 left-8">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em]" style={{ color: "#22D3EE" }}>The studio</p>
            <p className="mt-2 font-display font-bold text-2xl md:text-4xl tracking-tight text-white">Where tomorrow gets compiled.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

const AboutCta = () => (
  <section data-testid="about-cta" className="py-32 md:py-44 border-t border-ink/10 relative overflow-hidden">
    <div className="aurora aurora-float w-[22rem] h-[22rem] -top-10 right-10 opacity-20 -z-10" style={{ background: "#0096C7" }} aria-hidden="true" />
    <div className="aurora aurora-float w-[18rem] h-[18rem] bottom-0 -left-16 opacity-20 -z-10" style={{ background: "#FF8A00", animationDelay: "5s" }} aria-hidden="true" />
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <FadeUp>
        <Overline>Join us</Overline>
        <h2 className="mt-6 font-display font-black text-4xl md:text-7xl tracking-tighter leading-[0.98] max-w-4xl">
          Let's write the <span className="brand-gradient-text">next chapter</span> together.
        </h2>
        <Link
          to="/contact"
          data-testid="about-cta-button"
          className="group mt-12 inline-flex items-center gap-2 text-white font-semibold rounded-full pl-7 pr-6 py-4 bg-[linear-gradient(100deg,#FF8A00,#E0146B,#0096C7)] bg-[length:200%_100%] bg-left transition-[background-position] duration-500 hover:bg-right"
        >
          Work with us
          <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:rotate-45" />
        </Link>
      </FadeUp>
    </div>
  </section>
);

export default function AboutPage() {
  return (
    <>
      <AboutHero />
      <Story />
      <Mission />
      <Manifesto />
      <EditorialMarquee />
      <StudioImage />
      <AboutCta />
    </>
  );
}
